import { axiosInstance } from '../../axiosApi';
import { oauthStartUrl } from '../auth/sso';

export type DriveProvider = 'google' | 'microsoft';

export type DriveConnectionKind = 'personal' | 'company';

/** intent query param sent to /auth/oauth/:provider/start/ for drive-linking flows (#83/#84). */
export type DriveConnectIntent = 'link_drive' | 'link_company_drive';

export type DriveConnectionType = {
  id: number;
  provider: DriveProvider;
  kind?: DriveConnectionKind;
  is_active?: boolean;
  external_account_email?: string | null;
  selected_resource_ids?: string[];
  selected_resource_labels?: string[];
  last_sync_at?: string | null;
  last_sync_status?: string | null;
  last_sync_error?: string;
  created?: string;
};

export async function fetchDriveConnections(): Promise<DriveConnectionType[]> {
  const { data } = await axiosInstance.get<DriveConnectionType[]>('/drive/connections/');
  return Array.isArray(data) ? data : [];
}

export async function disconnectDriveConnection(connectionId: number): Promise<void> {
  await axiosInstance.delete(`/drive/connections/${connectionId}/`);
}

export type DriveResourceSelection = {
  resource_ids: string[];
  resource_labels: string[];
};

export async function saveDriveResourceSelection(
  connectionId: number,
  selection: DriveResourceSelection
): Promise<DriveConnectionType> {
  const { data } = await axiosInstance.post<DriveConnectionType>(
    `/drive/connections/${connectionId}/resources/`,
    selection
  );
  return data;
}

export async function syncDriveConnection(connectionId: number): Promise<DriveConnectionType | null> {
  const { data } = await axiosInstance.post<{
    connection?: DriveConnectionType;
  } & DriveConnectionType>(`/drive/connections/${connectionId}/sync/`);
  if (!data) return null;
  return data.connection || data;
}

/** Absolute backend OAuth start URL for a drive-link flow (personal or company). */
export function driveConnectUrl(provider: DriveProvider, intent: DriveConnectIntent): string {
  return oauthStartUrl(provider, intent);
}

/**
 * Begin Drive link: call start with Bearer JWT, then navigate to IdP authorize URL.
 * Full-page assign alone cannot send Authorization, so the backend returns JSON.
 */
export async function connectDrive(
  provider: DriveProvider,
  intent: DriveConnectIntent
): Promise<void> {
  const { data } = await axiosInstance.get<{ authorize_url: string }>(
    `/auth/oauth/${provider}/start/`,
    {
      params: { intent, response: 'json' },
      headers: { Accept: 'application/json' },
    }
  );
  if (!data?.authorize_url) {
    throw new Error('Drive connect did not return an authorize URL.');
  }
  window.location.assign(data.authorize_url);
}

/** Splits a comma/newline separated textarea/input value into trimmed, non-empty resource ids. */
export function parseResourceIdsInput(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((value) => value.trim())
    .filter(Boolean);
}
